import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {FIXTURE_STATE_CONFIG} from "@/utils/constants.js";
import {APP_STEPS} from "@/hooks/contest/contestSteps.js";
import {generatePseudo} from "@/utils/pseudoGenerator.js";
import {fromBase64Url} from "@/utils/base64Url.js";
import {getSmartView} from "@/utils/leaderboardEngine.js";
import {mapBackendToFrontend} from "@/utils/mappers.js";
import {StepBettingFootball} from "@/features/contest/StepBettingFootball.jsx";
import {StepLeaderboard} from "@/features/contest/StepLeaderboard.jsx";
import {StepResult} from "@/features/contest/StepResult.jsx";
import {GameLayout} from "@/features/contest/GameLayout.jsx";
import {StepBettingRugby} from "@/features/contest/StepBettingRugby.jsx";
import {makeMockRugbyLeaderboardBackend} from "@/mocks/mockRugbyLeaderboard.js";
import {makeMockFootballLeaderboardBackend} from "@/mocks/mockFootballLeaderboard.js";

const mockContestData = {
    isLoading: true,
    coverUrl: "",
    locale: "fr",
    fixture: {
        id: 0,
        startingAt: "2026-12-13 15:00:00",
        label: "",
        state: FIXTURE_STATE_CONFIG.NOT_STARTED,
        minutes: 0,
        extraMinutes: null,
        league: {
            name: "",
            logoUrl: "",
        },
        homeTeam: {
            name: "",
            logoUrl: "",
        },
        awayTeam: {
            name: "",
            logoUrl: "",
        },
    },
};

function deepMerge(base, patch) {
    if (!patch) return base;
    const out = Array.isArray(base) ? [...base] : {...base};
    for (const k of Object.keys(patch)) {
        const bv = base?.[k];
        const pv = patch[k];
        out[k] =
            pv &&
            typeof pv === "object" &&
            !Array.isArray(pv) &&
            bv &&
            typeof bv === "object"
                ? deepMerge(bv, pv)
                : pv;
    }
    return out;
}

function mapContestStepToAppStep(contestStep) {
    switch (contestStep) {
        case 2:
            return APP_STEPS.RESULT_WIN;
        case 3:
            return APP_STEPS.RESULT_LOSE;
        default:
            return APP_STEPS.GAME;
    }
}

const mockMatchScore = {team1: 0, team2: 0};
const mockCalculations = {
    total: 100,
    win: 50,
    score: 50,
    oddsWin: 2.0,
    oddsScore: 5.0,
};

const randomPseudo = generatePseudo()

export default function PreviewPage() {
    const {i18n} = useTranslation();

    const mockFromUrl = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get("mock");
        if (!raw) return null;
        try {
            return fromBase64Url(raw);
        } catch {
            return null;
        }
    }, []);

    const [contestData, setContestData] = useState(() =>
        deepMerge(mockContestData, mockFromUrl)
    );

    const [step, setStep] = useState(() =>
        mapContestStepToAppStep((mockFromUrl || {}).step)
    );

    const [fixture, setFixture] = useState(() =>
        mockContestData.fixture
    );

    useEffect(() => {
        if (contestData?.locale && i18n.language !== contestData.locale) {
            i18n.changeLanguage(contestData.locale);
        }
    }, [contestData.locale, i18n]);

    useEffect(() => {
        const onMsg = (e) => {
            if (!e?.data || e.data.type !== "GOJO_PREVIEW_UPDATE") return;
            const payload = e.data.payload;

            setContestData((prev) => deepMerge(prev, payload));
            if (typeof payload?.step !== "undefined") {
                setStep(mapContestStepToAppStep(payload.step));
                setFixture(payload.fixture || mockContestData.fixture);
            }
        };

        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, []);

    const [userScore, setUserScore] = useState({team1: 0, team2: 0});
    const [userBet, setUserBet] = useState({
        selection: null,
        margin: 0,
        winningTeamId: null
    });

    const handleRugbyBetChange = (field, value) => {
        setUserBet((prev) => ({...prev, [field]: value}));
    };

    const leaderboardBackend = useMemo(() => {
        if (fixture.type === 'RUGBY') {
            return makeMockRugbyLeaderboardBackend(
                fixture.homeTeam,
                fixture.awayTeam, 45,
                {
                    meIndex: 12,
                    startId: 30
                });
        } else {
            return makeMockFootballLeaderboardBackend(45, {
                meIndex: 12,
                startId: 30,
                targetScore: {
                    home: mockMatchScore.team1,
                    away: mockMatchScore.team2
                }
            });
        }
    }, [fixture]);

    const leaderboard = useMemo(
        () => getSmartView(mapBackendToFrontend(leaderboardBackend)),
        [leaderboardBackend]
    );

    const handleScoreChange = (team, op) => {
        setUserScore((prev) => {
            const current = prev[team];
            let next = op === "plus" ? current + 1 : current - 1;
            if (next < 0) next = 0;
            return {...prev, [team]: next};
        });
    };

    const renderContent = () => {
        if (step === APP_STEPS.GAME) {
            if (fixture.type === 'RUGBY') return (
                <StepBettingRugby
                    userBet={userBet}
                    fixture={fixture}
                    handleBetChange={handleRugbyBetChange}
                    calculations={mockCalculations}
                    onValidate={() => setStep(APP_STEPS.LEADERBOARD)}
                />
            );
            else {
                return (
                    <StepBettingFootball
                        userScore={userScore}
                        matchScore={mockMatchScore}
                        handleScoreChange={handleScoreChange}
                        calculations={mockCalculations}
                        onValidate={() => setStep(APP_STEPS.LEADERBOARD)}
                    />
                );
            }
        }

        if (step === APP_STEPS.LEADERBOARD) {
            return <StepLeaderboard leaderboard={leaderboard}/>;
        }

        if (step === APP_STEPS.RESULT_WIN || step === APP_STEPS.RESULT_LOSE) {
            const r =
                step === APP_STEPS.RESULT_WIN
                    ? contestData.winnerReward
                    : contestData.loserReward;

            return (
                <StepResult
                    status={step}
                    reward={r}
                    pseudo={randomPseudo}
                    triggerAnimation={false}
                    isPreview={true}
                />
            );
        }
    };

    return (
        <div className="h-screen overflow-y-auto font-sans bg-slate-50 text-gray-800">
            <style>{`
        .clip-image { mask-image: linear-gradient(to bottom, black 50%, transparent 100%); -webkit-mask-image: linear-gradient(to bottom, black 50%, transparent 100%); }
        .rendering-pixelated { image-rendering: pixelated; }
      `}</style>

            <div className="max-w-110 mx-auto min-h-full relative overflow-x-hidden shadow-2xl bg-white/50">
                {(() => {
                    const stepsWithLayout = [
                        APP_STEPS.GAME,
                        APP_STEPS.FORM,
                        APP_STEPS.LEADERBOARD,
                        APP_STEPS.MISSED,
                    ];
                    if (stepsWithLayout.includes(step)) {
                        return (
                            <GameLayout
                                contestData={contestData}
                                matchScore={mockMatchScore}
                                isGoalFlashing={false}
                            >
                                {renderContent()}
                            </GameLayout>
                        );
                    }
                    return renderContent();
                })()}
            </div>
        </div>
    );
}